// Template produit « hozo » : comportements partagés des sections et blocs hz-*.
// Chargé en module par snippets/hz-assets.liquid (plusieurs inclusions = une seule exécution).

/* ---------- Dialogues (détails produit, comparatif, garanties, accessoires) ----------
   <button data-hz-open="ID"> ouvre <dialog id="ID" class="hz-dialog">. showModal() fournit
   focus, Échap et fond ; un clic sur le fond ou [data-hz-close] ferme. */
document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-hz-open]');
  if (opener) {
    const dialog = document.getElementById(opener.dataset.hzOpen);
    if (dialog instanceof HTMLDialogElement) {
      // sorti du flux du bloc pour échapper aux overflow/transform des parents
      if (dialog.parentElement !== document.body) document.body.append(dialog);
      dialog.showModal();
      document.documentElement.classList.add('hz-lock');
    }
    return;
  }
  const dialog = event.target.closest('dialog.hz-dialog');
  if (!dialog) return;
  if (event.target.closest('[data-hz-close]') || event.target === dialog) dialog.close();
});
document.addEventListener(
  'close',
  (event) => {
    if (event.target.matches?.('dialog.hz-dialog')) document.documentElement.classList.remove('hz-lock');
  },
  true
);

/* ---------- Vidéos : source desktop/mobile choisie en JS, lecture quand visible ---------- */
const mobile = matchMedia('(max-width: 749px)');
const playWhenVisible = new IntersectionObserver(
  (entries) => {
    for (const { target, isIntersecting } of entries) {
      if (isIntersecting) {
        if (!target.dataset.hzPlayed || target.loop) target.play().catch(() => {});
      } else if (!target.paused) {
        target.pause();
      }
    }
  },
  { threshold: 0.35 }
);

class HzVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    if (!this.video) return;
    this.video.addEventListener('ended', () => (this.video.dataset.hzPlayed = '1'));
    this.pick();
    mobile.addEventListener('change', () => this.pick());
    playWhenVisible.observe(this.video);
    this.querySelector('[data-hz-toggle]')?.addEventListener('click', () => {
      if (this.video.paused) {
        delete this.video.dataset.hzPlayed;
        this.video.play();
      } else {
        this.video.pause();
      }
    });
    this.video.addEventListener('play', () => this.classList.add('is-playing'));
    this.video.addEventListener('pause', () => this.classList.remove('is-playing'));
  }

  pick() {
    const m = mobile.matches && this.dataset.srcMobile;
    const src = m ? this.dataset.srcMobile : this.dataset.src;
    const poster = m ? this.dataset.posterMobile : this.dataset.poster;
    if (poster) this.video.poster = poster;
    if (src && this.video.getAttribute('src') !== src) {
      this.video.src = src;
      this.style.setProperty('--hz-ratio', (m ? this.dataset.ratioMobile : this.dataset.ratio) || '');
    }
  }
}
customElements.get('hz-video') || customElements.define('hz-video', HzVideo);

/* ---------- Défilement horizontal avec flèches, points et numéros ---------- */
class HzSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-hz-track]');
    if (!this.track) return;
    this.slides = [...this.track.children];
    this.querySelectorAll('[data-hz-prev]').forEach((b) => b.addEventListener('click', () => this.go(this.index - 1)));
    this.querySelectorAll('[data-hz-next]').forEach((b) => b.addEventListener('click', () => this.go(this.index + 1)));
    this.querySelectorAll('[data-hz-goto]').forEach((b) =>
      b.addEventListener('click', () => this.go(Number(b.dataset.hzGoto)))
    );
    this.index = 0;
    let raf;
    this.track.addEventListener('scroll', () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => this.sync());
    });
    this.sync();
  }

  go(i) {
    const n = this.slides.length;
    const target = this.slides[((i % n) + n) % n];
    this.track.scrollTo({ left: target.offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
  }

  sync() {
    const left = this.track.scrollLeft;
    let best = 0;
    this.slides.forEach((s, i) => {
      if (Math.abs(s.offsetLeft - this.track.offsetLeft - left) < Math.abs(this.slides[best].offsetLeft - this.track.offsetLeft - left)) best = i;
    });
    if (best === this.index && this.dataset.synced) return;
    this.dataset.synced = '1';
    this.index = best;
    this.querySelectorAll('[data-hz-goto]').forEach((b) =>
      b.setAttribute('aria-current', String(Number(b.dataset.hzGoto) === best))
    );
    this.slides.forEach((s, i) => s.classList.toggle('is-active', i === best));
    this.dispatchEvent(new CustomEvent('hz:slide', { detail: { index: best, slide: this.slides[best] } }));
  }
}
customElements.get('hz-slider') || customElements.define('hz-slider', HzSlider);

/* ---------- Vidéos créateurs : façade YouTube, iframe injectée sur la diapo active ---------- */
class HzCreators extends HTMLElement {
  connectedCallback() {
    this.visible = false;
    new IntersectionObserver(([e]) => {
      this.visible = e.isIntersecting;
      this.update();
    }, { threshold: 0.4 }).observe(this);
    this.addEventListener('hz:slide', () => this.update());
    this.addEventListener('click', (event) => {
      const facade = event.target.closest('[data-yt]');
      if (facade) this.mount(facade, true);
    });
  }

  update() {
    const active = this.querySelector('.is-active [data-yt]');
    this.querySelectorAll('[data-yt]').forEach((f) => {
      if (f !== active || !this.visible) this.unmount(f);
    });
    if (active && this.visible) this.mount(active, false);
  }

  mount(facade, withSound) {
    if (facade.querySelector('iframe')) return;
    const id = facade.dataset.yt;
    const params = withSound
      ? 'autoplay=1&rel=0&playsinline=1'
      : `autoplay=1&mute=1&controls=0&loop=1&playlist=${id}&rel=0&playsinline=1`;
    facade.insertAdjacentHTML(
      'beforeend',
      `<iframe src="https://www.youtube-nocookie.com/embed/${id}?${params}" title="${facade.dataset.title || ''}" allow="autoplay; encrypted-media; picture-in-picture" allowfullscreen loading="lazy"></iframe>`
    );
  }

  unmount(facade) {
    facade.querySelector('iframe')?.remove();
  }
}
customElements.get('hz-creators') || customElements.define('hz-creators', HzCreators);

/* ---------- Add-ons cochés ajoutés avec le produit principal ----------
   Horizon câble on:submit sur document en phase capture : on intercepte avant, sur window,
   UNIQUEMENT le formulaire principal de la section (pas les quick-add des recommandations).
   Les add-ons partent d'abord ; le produit principal est soumis quoi qu'il arrive, et son rendu
   du tiroir panier inclut alors les add-ons. */
const RESUBMIT = Symbol('hz-resubmit');
window.addEventListener(
  'submit',
  async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form[RESUBMIT]) return;
    // getAttribute : le formulaire contient un <input name="id">, donc form.id renvoie cet input
    const upsell = document.querySelector(`hz-upsell[data-form-id="${form.getAttribute('id')}"]`);
    if (!upsell) return;
    const items = upsell.checkedItems();
    if (!items.length) return;
    event.preventDefault();
    event.stopPropagation();
    await upsell.add(items);
    form[RESUBMIT] = true;
    form.requestSubmit(event.submitter && form.contains(event.submitter) ? event.submitter : undefined);
    form[RESUBMIT] = false;
  },
  true
);

class HzUpsell extends HTMLElement {
  checkedItems() {
    return [...this.querySelectorAll('input[type="checkbox"]:checked')].map((c) => ({
      id: Number(c.value),
      quantity: 1,
    }));
  }

  async add(items) {
    const error = this.querySelector('[data-hz-error]');
    error.hidden = true;
    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) throw new Error((await res.json()).description || res.statusText);
      this.querySelectorAll('input[type="checkbox"]:checked').forEach((c) => (c.checked = false));
    } catch (e) {
      error.textContent = `${this.dataset.errorText} ${e.message || ''}`.trim();
      error.hidden = false;
    }
  }
}
customElements.get('hz-upsell') || customElements.define('hz-upsell', HzUpsell);

/* ---------- Sélecteur d'objectifs : un clic (ou ←/→) change la vue à l'écran ---------- */
class HzLenses extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => this.select(i));
      tab.addEventListener('keydown', (event) => {
        const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
        if (event.key === 'Home' || event.key === 'End' || step) {
          event.preventDefault();
          const n = this.tabs.length;
          const next = event.key === 'Home' ? 0 : event.key === 'End' ? n - 1 : (i + step + n) % n;
          this.select(next);
          this.tabs[next].focus();
        }
      });
    });
  }

  select(index) {
    this.tabs.forEach((tab, i) => {
      const on = i === index;
      tab.setAttribute('aria-selected', String(on));
      tab.tabIndex = on ? 0 : -1;
      const panel = document.getElementById(tab.getAttribute('aria-controls'));
      if (!panel) return;
      panel.hidden = !on;
      // relance l'animation de zoom à chaque sélection
      panel.classList.remove('is-active');
      if (on) {
        void panel.offsetWidth;
        panel.classList.add('is-active');
      }
    });
  }
}
customElements.get('hz-lenses') || customElements.define('hz-lenses', HzLenses);

/* ---------- Avant / après : déplacé à la main uniquement ----------
   Glisser (souris, doigt, stylet) n'importe où sur l'image ; le défilement vertical de la page
   reste possible au doigt (touch-action: pan-y). Le <input type=range> invisible porte le clavier
   et l'accessibilité, et reste synchronisé. */
class HzCompare extends HTMLElement {
  connectedCallback() {
    this.range = this.querySelector('.hz-ba__range');
    this.range?.addEventListener('input', () => this.set(Number(this.range.value)));
    // pas de glisser-déposer natif des images (image fantôme qui « déplace la photo »)
    this.addEventListener('dragstart', (event) => event.preventDefault());
    this.addEventListener('pointerdown', (event) => {
      // un seul doigt pilote le curseur : un 2e pointeur (pincement) n'interrompt pas le geste
      if (event.button !== 0 || (this.dragging && event.pointerId !== this.pointerId)) return;
      // souris : empêche la sélection de texte et le drag natif dès l'appui
      if (event.pointerType === 'mouse') event.preventDefault();
      this.dragging = true;
      this.pointerId = event.pointerId;
      this.startX = event.clientX;
      this.startY = event.clientY;
      this.decided = event.pointerType === 'mouse';
      if (this.decided) {
        this.setPointerCapture(event.pointerId);
        this.fromPointer(event);
      }
    });
    this.addEventListener('pointermove', (event) => {
      if (!this.dragging || event.pointerId !== this.pointerId) return;
      if (!this.decided) {
        // au doigt : on ne prend la main que si le geste est horizontal (sinon c'est un scroll)
        const dx = Math.abs(event.clientX - this.startX);
        const dy = Math.abs(event.clientY - this.startY);
        if (dx < 6 && dy < 6) return;
        if (dy > dx) {
          this.dragging = false;
          return;
        }
        // Pas de setPointerCapture au doigt : le navigateur a déjà capturé le pointeur sur
        // l'élément touché, et les pointermove remontent jusqu'ici.
        this.decided = true;
      }
      this.fromPointer(event);
    });
    const stop = (event) => {
      if (event.pointerId === this.pointerId) this.dragging = false;
    };
    this.addEventListener('pointerup', stop);
    this.addEventListener('pointercancel', stop);
    // Geste horizontal engagé : on verrouille le défilement vertical de la page jusqu'au lever
    // du doigt (sinon une composante verticale rend la main au scroll et annule le glisser).
    // touchmove arrive après pointermove pour le même mouvement : la décision est déjà prise.
    this.addEventListener(
      'touchmove',
      (event) => {
        if (this.dragging && this.decided && event.touches.length === 1 && event.cancelable) event.preventDefault();
      },
      { passive: false }
    );
    // lostpointercapture REMONTE depuis les calques enfants (capture implicite au doigt) :
    // on ne s'arrête que sur la perte de NOTRE capture, sinon le geste meurt au 1er mouvement.
    this.addEventListener('lostpointercapture', (event) => {
      if (event.target === this) stop(event);
    });
  }

  fromPointer(event) {
    const rect = this.getBoundingClientRect();
    this.set(((event.clientX - rect.left) / rect.width) * 100);
  }

  set(pos) {
    const p = Math.min(100, Math.max(0, pos));
    this.style.setProperty('--pos', `${p}%`);
    if (this.range) this.range.value = String(Math.round(p));
  }
}
customElements.get('hz-compare') || customElements.define('hz-compare', HzCompare);
